import type { ReactNode } from 'react'
import { Select } from './form'
import { Button } from './primitives'

/**
 * The controls every listing carries: filters, a sort, and a way through the pages.
 *
 * One component rather than one per screen. A rep who learns where the filter lives on
 * Quotations should not have to learn it again on Customers, and a listing that quietly
 * lacks paging is a listing that silently stops at row 25.
 */

export interface SortChoice {
  /** Wire value: `field` ascending, `-field` descending. */
  value: string
  label: string
}

export function ListToolbar({
  children,
  sort,
  sortChoices,
  onSortChange,
}: {
  /** Filters, in source order. Each should be a labelled control, not a bare input. */
  children?: ReactNode
  sort: string
  sortChoices: SortChoice[]
  onSortChange: (sort: string) => void
}) {
  return (
    <div className="mb-4 flex flex-wrap items-end gap-3">
      {children}

      {/* Pushed to the end: sort answers "in what order", which is read after "which
          rows", and keeping it in the same corner on every screen makes it findable. */}
      <label className="ml-auto flex items-center gap-2 text-[13px] text-muted">
        <span className="whitespace-nowrap">Sort by</span>
        <Select
          aria-label="Sort by"
          value={sort}
          onChange={(event) => onSortChange(event.target.value)}
          className="h-9 w-48 py-0 text-sm"
        >
          {sortChoices.map((choice) => (
            <option key={choice.value} value={choice.value}>
              {choice.label}
            </option>
          ))}
        </Select>
      </label>
    </div>
  )
}

/** A labelled filter dropdown. Every filter on every listing looks like this one. */
export function FilterSelect({
  label,
  value,
  onChange,
  children,
  width = 'w-44',
}: {
  label: string
  value: string
  onChange: (value: string) => void
  children: ReactNode
  width?: string
}) {
  return (
    <label className="flex items-center gap-2 text-[13px] text-muted">
      <span className="whitespace-nowrap">{label}</span>
      <Select
        aria-label={label}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className={`h-9 ${width} py-0 text-sm`}
      >
        {children}
      </Select>
    </label>
  )
}

/** Offered on every listing. 10 is the default: a short page is a page you can scan. */
export const PAGE_SIZES = [10, 25, 50, 100] as const

export const DEFAULT_PAGE_SIZE = 10

/**
 * Page controls, and the count that makes them mean something.
 *
 * The footer always renders, even for six rows on a single page. It used to hide itself
 * whenever everything fit, which sounds tidy and is not: the row count and the rows-per-page
 * control are exactly how someone confirms they are seeing everything, and hiding them
 * means the one listing that silently truncates looks identical to the one that did not.
 * What is conditional is Previous/Next - buttons that cannot go anywhere.
 */
export function Pagination({
  page,
  pageSize,
  totalCount,
  totalPages,
  onPageChange,
  onPageSizeChange,
}: {
  page: number
  pageSize: number
  totalCount: number
  totalPages: number
  onPageChange: (page: number) => void
  /** Omit on a fixed-size list; the control then does not appear. */
  onPageSizeChange?: (pageSize: number) => void
}) {
  const first = totalCount === 0 ? 0 : (page - 1) * pageSize + 1
  const last = Math.min(page * pageSize, totalCount)

  return (
    <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-t border-line-soft px-5 py-3">
      <p className="text-[12.5px] text-muted tabular">
        {totalCount === 0 ? 'No rows' : `${first}\u2013${last} of ${totalCount}`}
      </p>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        {onPageSizeChange && (
          <label className="flex items-center gap-2 text-[12.5px] text-muted">
            <span className="whitespace-nowrap">Rows</span>
            <Select
              aria-label="Rows per page"
              value={pageSize}
              onChange={(event) => onPageSizeChange(Number(event.target.value))}
              className="h-8 w-24 py-0 text-[12.5px]"
            >
              {PAGE_SIZES.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </Select>
          </label>
        )}

        {totalPages > 1 && (
          <div className="flex items-center gap-2">
            <Button size="sm" disabled={page <= 1} onClick={() => onPageChange(page - 1)}>
              Previous
            </Button>
            <span className="text-[12.5px] text-muted tabular">
              Page {page} of {totalPages}
            </span>
            <Button size="sm" disabled={page >= totalPages} onClick={() => onPageChange(page + 1)}>
              Next
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
